import { Button } from "@/components/ui/baseUi/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/baseUi/dropdown-menu"
import { MonthPickerCalendar } from "@/components/ui/monthPickerCalendar"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/baseUi/popover"
import { Text } from "@/components/ui/baseUi/text"
import { useCurrentBox } from "@/modules/box/hooks/useBox"
import { BoxStaffPermissions } from "@/modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffPermission } from "@/modules/boxStaff/hooks/useBoxStaffPermissions"
import { word } from "@/modules/core/utils/i18n"
import { useFinanceStats } from "@/modules/finances/hooks/useFinance"
import { usePeriodParam } from "@/modules/finances/hooks/usePeriod"
import { useStripeAccount } from "@/modules/finances/hooks/useStripeAccount"
import { useTaxes } from "@/modules/finances/taxes/hooks/useTaxes"
import { cn } from "@/modules/lib/utils"
import { useBoxNavigate } from "@/modules/navigation/hooks/useBoxNavigate"
import { useMySpaceNavigate } from "@/modules/navigation/hooks/useMySpaceNavigate"
import { Path } from "@/modules/navigation/routes"
import { StripeAccountStatus } from "@/modules/payment/stripeTypes"
import { useUser } from "@/modules/users/hooks/useUser"
import { monthYearDateFormat } from "@/modules/utils/dateUtils"
import dayjs from "dayjs"
import { ArrowLeftIcon, CalendarIcon, ExternalLinkIcon, LoaderCircle, PercentIcon, SettingsIcon } from "lucide-react"
import { useState } from "react"

type FinancesPageHeaderProps = {
  pageName: string
  subPageName?: string
  displayMonthPicker?: boolean
  displaySettingsButton?: boolean
  className?: string
} & React.ComponentProps<"div">

export function BoxFinancesPageHeader({ ...props }: FinancesPageHeaderProps) {
  const { result: box } = useCurrentBox()
  const canSeeBankDetails = useBoxStaffPermission(BoxStaffPermissions.SEE_BANK_DETAILS)
  const canEditFinancial = useBoxStaffPermission(BoxStaffPermissions.EDIT_FINANCIAL)
  const boxNavigate = useBoxNavigate()
  const { openTaxSettings } = useTaxes()
  const isStripeAccountComplete = box?.stripeAccountStatus === StripeAccountStatus.OK

  const onNavigateBackToFinances = () => {
    boxNavigate(Path.Box.Finances.Root)
  }

  const onOpenStripeAccount = () => {
    boxNavigate(Path.Box.Admin + "?tab=banking")
  }

  const onOpenTaxSettings = () => {
    openTaxSettings()
  }

  return (
    <FinancesPageHeaderComponent
      onNavigateBackToFinances={onNavigateBackToFinances}
      onOpenStripeAccount={onOpenStripeAccount}
      onOpenTaxSettings={onOpenTaxSettings}
      isStripeAccountComplete={isStripeAccountComplete}
      minDate={box?.createdAt ? new Date(box.createdAt) : undefined}
      canSeeBankDetails={canSeeBankDetails}
      canEditFinancial={canEditFinancial}
      {...props}
    />
  )
}
export function UserFinancesPageHeader({ ...props }: FinancesPageHeaderProps) {
  const [periodParam] = usePeriodParam()
  const user = useUser()
  const { updateStripeAccount, loading: isLoadingStripeAccount } = useStripeAccount()
  const mySpaceNavigate = useMySpaceNavigate()
  const isStripeAccountComplete = user.stripeAccountStatus === StripeAccountStatus.OK
  const { data: stats } = useFinanceStats(isStripeAccountComplete, periodParam)

  const onNavigateBackToFinances = () => {
    mySpaceNavigate(Path.MySpace.Finances.Root)
  }

  const onOpenStripeAccount = () => {
    if (
      user.stripeAccountStatus === StripeAccountStatus.OK ||
      user.stripeAccountStatus === StripeAccountStatus.REQUIRE_ADDITIONAL_INFORMATION ||
      user.stripeAccountStatus === StripeAccountStatus.ONBOARDING
    )
      updateStripeAccount()
    else throw new Error("Stripe account is not created")
  }

  return (
    <FinancesPageHeaderComponent
      onNavigateBackToFinances={onNavigateBackToFinances}
      onOpenStripeAccount={onOpenStripeAccount}
      isStripeAccountComplete={isStripeAccountComplete}
      isLoadingStripeAccount={isLoadingStripeAccount}
      minDate={stats?.firstPeriod ? new Date(stats.firstPeriod) : new Date(user.inscriptionDate)}
      canSeeBankDetails={true}
      canEditFinancial={true}
      {...props}
    />
  )
}

type FinancesPageHeaderComponentProps = {
  onNavigateBackToFinances: () => void
  onOpenStripeAccount: () => void
  onOpenTaxSettings?: () => void
  isStripeAccountComplete: boolean
  isLoadingStripeAccount?: boolean
  canSeeBankDetails: boolean
  canEditFinancial: boolean
  minDate?: Date
} & FinancesPageHeaderProps

function FinancesPageHeaderComponent({
  pageName,
  subPageName,
  displayMonthPicker,
  displaySettingsButton,
  onNavigateBackToFinances,
  onOpenStripeAccount,
  onOpenTaxSettings,
  isStripeAccountComplete,
  isLoadingStripeAccount = false,
  canSeeBankDetails,
  canEditFinancial,
  minDate,
  className,
  ...props
}: FinancesPageHeaderComponentProps) {
  const [periodParam, setPeriodParam] = usePeriodParam()
  const [isMonthPickerOpen, setIsMonthPickerOpen] = useState(false)
  const [period, setPeriod] = useState<string | undefined>(periodParam)

  const { isLoading: isLoadingStats } = useFinanceStats(isStripeAccountComplete, periodParam)

  const month = dayjs(period).month()
  const year = dayjs(period).year()

  const getMonthLabel = () => {
    if (period) {
      return dayjs(period).format("MMMM YY")
    }
    return dayjs().format("MMMM YY")
  }

  const handleMonthSelect = (monthIndex: number) => {
    setPeriod(dayjs().year(year).month(monthIndex).format(monthYearDateFormat))
    setPeriodParam(dayjs().year(year).month(monthIndex).format(monthYearDateFormat))
    setIsMonthPickerOpen(false)
  }

  const handleYearChange = (year: number) => {
    setPeriod(dayjs().year(year).format(monthYearDateFormat))
  }

  return (
    <div className={cn(`flex min-h-8.5 w-full flex-row items-start justify-between gap-2`, className)} {...props}>
      <div className="flex flex-row items-baseline gap-2">
        {subPageName ? (
          <div className="flex flex-row items-center gap-2">
            <Button variant="secondary" size="iconSm" onClick={onNavigateBackToFinances}>
              <ArrowLeftIcon color="currentColor" />
            </Button>
            <Button variant="underline" size="lg" onClick={onNavigateBackToFinances} className="px-0">
              {pageName}
            </Button>
            <Text fontStyle="HS">/</Text>
            <Text fontStyle="HS">{subPageName}</Text>
          </div>
        ) : (
          <Text fontStyle="HL">{pageName}</Text>
        )}

        {displayMonthPicker && (
          <Popover open={isMonthPickerOpen} onOpenChange={setIsMonthPickerOpen}>
            <PopoverTrigger
              className="px-0"
              render={
                <button className="flex cursor-pointer items-start gap-2 rounded-r-full bg-surface-background px-4 py-3">
                  <span className="text-sm">{getMonthLabel()}</span>
                  {isLoadingStats ? (
                    <LoaderCircle className="size-4 animate-spin" color="currentColor" />
                  ) : (
                    <CalendarIcon className="size-4" color="currentColor" />
                  )}
                </button>
              }
            />

            <PopoverContent className="w-64 rounded-2xl border-none bg-surface-background-float p-4" align="end">
              <MonthPickerCalendar
                year={year}
                month={month}
                onSelectMonth={handleMonthSelect}
                onYearChange={handleYearChange}
                minDate={minDate}
              />
            </PopoverContent>
          </Popover>
        )}
      </div>

      {displaySettingsButton && canSeeBankDetails && (
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <Button variant="secondary" size="iconMd" data-testid="settings-menu" disabled={isLoadingStripeAccount}>
                {isLoadingStripeAccount ? (
                  <LoaderCircle className="size-4 animate-spin" color="currentColor" />
                ) : (
                  <SettingsIcon className="size-4" />
                )}
              </Button>
            }
          />
          <DropdownMenuContent className="mr-4 w-fit">
            <Text fontStyle="LS">{word("finances.page.settings.title")}</Text>
            <DropdownMenuItem onClick={onOpenStripeAccount}>
              <ExternalLinkIcon className="size-4" />
              {word("finances.page.settings.manageBankDetails")}
            </DropdownMenuItem>
            {canEditFinancial && !!onOpenTaxSettings && (
              <DropdownMenuItem onClick={onOpenTaxSettings}>
                <PercentIcon className="size-4" />
                {word("finances.page.settings.manageTaxes")}
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  )
}
